import {
    ORGANIZATION_GROUP_SKILL_EXCLUSION_BATCH_LIMIT,
    type IOrganizationGroupSkillMinimal,
} from "@/api/organization-groups";
import { Button } from "@/components/ui/button";
import {
    Combobox,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxItem,
    ComboboxList,
    useComboboxAnchor,
} from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { XIcon } from "lucide-react";
import { useId, useState, type Ref } from "react";

export interface ExcludedSkillsComboboxProps {
    options: readonly IOrganizationGroupSkillMinimal[];
    value: IOrganizationGroupSkillMinimal[];
    onValueChange: (value: IOrganizationGroupSkillMinimal[]) => void;
    onExclude: () => void;
    loading?: boolean;
    disabled?: boolean;
    excluding?: boolean;
    error?: string | null;
    disabledReason?: string | null;
    inputRef?: Ref<HTMLInputElement>;
}

export function ExcludedSkillsCombobox({
    options,
    value,
    onValueChange,
    onExclude,
    loading = false,
    disabled = false,
    excluding = false,
    error = null,
    disabledReason = null,
    inputRef,
}: ExcludedSkillsComboboxProps) {
    const inputId = useId();
    const hintId = useId();
    const validationId = useId();
    const errorId = useId();
    const anchor = useComboboxAnchor();
    const [showValidation, setShowValidation] = useState(false);

    const exhausted = !loading && options.length === 0 && value.length === 0;
    const reason =
        disabledReason ??
        (exhausted
            ? "Every global skill is already excluded from this group."
            : null);
    const fieldDisabled = disabled || loading || reason !== null;
    const atLimit =
        value.length >= ORGANIZATION_GROUP_SKILL_EXCLUSION_BATCH_LIMIT;
    const selectedIds = new Set(value.map((skill) => skill.id));
    const validation =
        showValidation && value.length === 0
            ? "Select at least one skill to exclude."
            : null;
    const hint =
        reason ??
        (atLimit
            ? `${ORGANIZATION_GROUP_SKILL_EXCLUSION_BATCH_LIMIT} skills selected, the most one request can exclude.`
            : null);
    const describedBy =
        [
            hint ? hintId : null,
            validation ? validationId : null,
            error ? errorId : null,
        ]
            .filter(Boolean)
            .join(" ") || undefined;

    const changeSelection = (
        next: IOrganizationGroupSkillMinimal[],
        details: BaseCombobox.Root.ChangeEventDetails
    ) => {
        if (details.reason === "escape-key") return;
        if (next.length > ORGANIZATION_GROUP_SKILL_EXCLUSION_BATCH_LIMIT) {
            return;
        }
        setShowValidation(false);
        onValueChange(next);
    };

    const exclude = () => {
        if (value.length === 0) {
            setShowValidation(true);
            return;
        }
        onExclude();
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="grid min-w-0 flex-1 gap-2">
                    <Label htmlFor={inputId}>Global skills to exclude</Label>
                    <Combobox
                        multiple
                        items={options}
                        value={value}
                        onValueChange={changeSelection}
                        itemToStringLabel={(
                            skill: IOrganizationGroupSkillMinimal
                        ) => skill.name}
                        itemToStringValue={(
                            skill: IOrganizationGroupSkillMinimal
                        ) => String(skill.id)}
                        isItemEqualToValue={(
                            item: IOrganizationGroupSkillMinimal,
                            selected: IOrganizationGroupSkillMinimal
                        ) => item.id === selected.id}
                        openOnInputClick
                        disabled={fieldDisabled}
                    >
                        <ComboboxChips ref={anchor} className="min-w-0">
                            {loading && (
                                <span className="text-muted-foreground text-sm">
                                    Loading skills…
                                </span>
                            )}
                            {value.map((skill) => (
                                <ComboboxChip
                                    key={skill.id}
                                    showRemove={false}
                                    className="bg-muted/40 h-6 max-w-full min-w-0 rounded-md border pr-0 pl-2"
                                >
                                    <span
                                        title={skill.name}
                                        className="min-w-0 truncate"
                                    >
                                        {skill.name}
                                    </span>
                                    <BaseCombobox.ChipRemove
                                        aria-label={`Remove ${skill.name} from selection`}
                                        data-slot="combobox-chip-remove"
                                        render={
                                            <Button
                                                variant="ghost"
                                                size="icon-xs"
                                                disabled={disabled}
                                            />
                                        }
                                        className="text-muted-foreground hover:text-foreground size-5"
                                    >
                                        <XIcon className="pointer-events-none" />
                                    </BaseCombobox.ChipRemove>
                                </ComboboxChip>
                            ))}
                            <ComboboxChipsInput
                                id={inputId}
                                ref={inputRef}
                                aria-describedby={describedBy}
                                aria-disabled={fieldDisabled}
                                aria-invalid={error ? true : undefined}
                                placeholder={
                                    fieldDisabled || value.length > 0
                                        ? ""
                                        : "Select skills…"
                                }
                            />
                        </ComboboxChips>
                        <ComboboxContent anchor={anchor}>
                            <ComboboxEmpty>
                                {options.length === 0
                                    ? "No other global skills to select."
                                    : "No matching skills."}
                            </ComboboxEmpty>
                            <ComboboxList>
                                {(skill: IOrganizationGroupSkillMinimal) => (
                                    <ComboboxItem
                                        key={skill.id}
                                        value={skill}
                                        disabled={
                                            atLimit &&
                                            !selectedIds.has(skill.id)
                                        }
                                    >
                                        <span className="min-w-0 break-words">
                                            {skill.name}
                                        </span>
                                    </ComboboxItem>
                                )}
                            </ComboboxList>
                        </ComboboxContent>
                    </Combobox>
                </div>
                <Button
                    type="button"
                    className="shrink-0"
                    onClick={exclude}
                    disabled={fieldDisabled}
                    aria-disabled={fieldDisabled}
                    aria-describedby={reason ? hintId : undefined}
                >
                    {excluding ? "Excluding…" : "Exclude"}
                </Button>
            </div>
            {hint && (
                <p id={hintId} className="text-muted-foreground text-xs">
                    {hint}
                </p>
            )}
            {validation && (
                <p
                    id={validationId}
                    role="alert"
                    className="text-destructive text-sm"
                >
                    {validation}
                </p>
            )}
            {error && (
                <p
                    id={errorId}
                    role="alert"
                    className="text-destructive text-sm"
                >
                    {error}
                </p>
            )}
        </div>
    );
}
